import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Users, Calendar, LogOut, Loader2, Search, Plus, ChevronLeft, Mail, Phone, MapPin,
  DollarSign, X, Trash2, ShieldAlert, RefreshCw, Download, BarChart3, Star, MessageSquare,
  Package, Save, Edit3, TrendingUp, AlertTriangle, Send, FileText, ClipboardList,
  ArrowUp, ArrowDown, ArrowUpDown, ImagePlus
} from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return s; }
};

// ===================== LOGIN VIEW =====================
const LoginView = ({ error }) => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL — derived from window.location.origin
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1D4E41] to-[#2d6a4f] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center">
          <ShieldAlert className="w-10 h-10 text-[#1D4E41]" />
        </div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] mb-3">Panel Administrativo</h1>
        <p className="text-gray-600 mb-8">Inicia sesión con tu cuenta de Google autorizada para acceder al panel.</p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            Error de autenticación. Intenta de nuevo.
          </div>
        )}
        <button
          onClick={handleLogin}
          data-testid="google-login-btn"
          className="w-full h-12 bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 rounded-full font-semibold flex items-center justify-center gap-3 transition-all hover:shadow-md"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar con Google
        </button>
        <p className="mt-6 text-xs text-gray-400">Travel Guide Costa Rica · Sólo personal autorizado</p>
      </div>
    </div>
  );
};

// ===================== ADD CUSTOMER MODAL =====================
const AddCustomerModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', notes: '',
    tour_title: '', date: '', passengers: 1, total_price: '', source: 'email'
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.adminCreateCustomer({
        ...form,
        passengers: form.passengers ? parseInt(form.passengers) : null,
        total_price: form.total_price ? parseFloat(form.total_price) : null,
        tour_title: form.tour_title || null,
        date: form.date || null
      });
      toast.success('Cliente histórico agregado');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-xl font-bold text-[#1A1A1A]">Agregar Cliente Histórico</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-add-modal"><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuente</label>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none">
                <option value="email">Email histórico</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="stripe">Stripe</option>
                <option value="manual">Manual / Otro</option>
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-3">Reserva pasada (opcional)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tour realizado</label>
                <input value={form.tour_title} onChange={(e) => setForm({ ...form, tour_title: e.target.value })} placeholder="Ej: ATV Adventure 3 Hours" className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del tour</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pasajeros</label>
                <input type="number" min="1" value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Total pagado (USD)</label>
                <input type="number" step="0.01" min="0" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas internas</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Cualquier información relevante..." className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none resize-none" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 h-12 border border-gray-200 text-gray-700 rounded-full font-medium hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="cust-save" className="flex-1 h-12 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              Guardar Cliente
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===================== CUSTOMER DETAIL DRAWER =====================
const CustomerDetailDrawer = ({ email, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const d = await api.adminGetCustomer(email);
        setData(d);
      } catch (e) { toast.error('Error cargando detalle'); }
      finally { setLoading(false); }
    })();
  }, [email]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-drawer">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-[#1A1A1A]">Detalle del Cliente</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>
        ) : data ? (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Contacto</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-1 text-sm">
                <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /> {data.email}</p>
                {data.manual?.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /> {data.manual.phone}</p>}
                {data.manual?.notes && <p className="mt-2 text-gray-600 italic">&ldquo;{data.manual.notes}&rdquo;</p>}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Reservas ({data.bookings.length})</h3>
              {data.bookings.length === 0 ? (
                <p className="text-gray-400 text-sm italic">Sin reservas registradas</p>
              ) : (
                <div className="space-y-2">
                  {data.bookings.map((b, i) => (
                    <div key={b.id || i} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="font-medium text-[#1A1A1A]">{b.tour_title}</p>
                          <p className="text-xs text-gray-500 mt-1">{fmtDate(b.date)} · {b.passengers || 1} pax · {fmtMoney(b.total_price)}</p>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{b.payment_status || 'pending'}</span>
                          {b.is_historical && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">histórico</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Encuestas ({data.surveys.length})</h3>
              {data.surveys.length === 0 ? (
                <p className="text-gray-400 text-sm italic">Sin encuestas</p>
              ) : (
                <div className="space-y-2">
                  {data.surveys.map((s, i) => (
                    <div key={s.id || i} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= (s.overall_rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />)}
                        <span className="ml-auto text-xs text-gray-400">NPS {s.nps ?? '—'}</span>
                      </div>
                      <p className="text-sm text-gray-600 italic">&ldquo;{s.comment || 'Sin comentario'}&rdquo;</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ===================== CUSTOMERS TAB =====================
const CustomersTab = () => {
  const [data, setData] = useState({ total: 0, customers: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminListCustomers(search);
      setData(d);
    } catch (e) { toast.error('Error cargando clientes'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const exportCsv = () => {
    const rows = [['Nombre', 'Email', 'Teléfono', 'Reservas', 'Total gastado (USD)', 'Última fecha', 'Último tour', 'Fuente', 'Notas']];
    data.customers.forEach(c => rows.push([
      c.name, c.email, c.phone, c.bookings_count, c.total_spent, c.last_booking_date || '', c.last_tour || '', c.source, (c.notes || '').replace(/[\r\n]+/g, ' ')
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientes-travelguide-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Buscar por nombre, email o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="customer-search"
            className="w-full h-11 pl-10 pr-3 border border-gray-200 rounded-full focus:border-[#1D4E41] outline-none"
          />
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-customer-btn" className="h-11 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-semibold flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Agregar Cliente
        </button>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="h-11 px-5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full font-medium flex items-center justify-center gap-2">
          <Download className="w-4 h-4" /> Exportar CSV
        </button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Clientes</p>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data.total}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Recurrentes</p>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data.customers.filter(c => c.bookings_count > 1).length}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Ingresos totales</p>
          <p className="text-2xl font-bold text-[#1A1A1A]">{fmtMoney(data.customers.reduce((s, c) => s + (c.total_spent || 0), 0))}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Históricos</p>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data.customers.filter(c => c.source.includes('manual') || c.source === 'email' || c.source === 'whatsapp' || c.source === 'stripe').length}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>
      ) : data.customers.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Sin clientes encontrados</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Contacto</th>
                  <th className="text-right px-4 py-3">Reservas</th>
                  <th className="text-right px-4 py-3">Gastado</th>
                  <th className="text-left px-4 py-3">Última fecha</th>
                  <th className="text-left px-4 py-3">Fuente</th>
                  <th className="px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.customers.map((c, idx) => (
                  <tr key={c.email + idx} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedEmail(c.email)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1A1A1A]">{c.name || '—'}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[200px]">{c.last_tour}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="truncate max-w-[180px]">{c.email}</div>
                      <div className="text-xs text-gray-400">{c.phone || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{c.bookings_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#1D4E41]">{fmtMoney(c.total_spent)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(c.last_booking_date)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.source === 'app' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{c.source}</span>
                    </td>
                    <td className="px-2"><ChevronLeft className="w-4 h-4 text-gray-300 rotate-180" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && <AddCustomerModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); fetchData(); }} />}
      {selectedEmail && <CustomerDetailDrawer email={selectedEmail} onClose={() => setSelectedEmail(null)} />}
    </div>
  );
};

// ===================== BOOKINGS TAB =====================
const SortableTh = ({ field, label, align = 'left', sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        data-testid={`sort-${field}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold transition-colors hover:text-[#1D4E41] ${active ? 'text-[#1D4E41]' : 'text-gray-600'} ${align === 'right' ? 'flex-row-reverse' : ''}`}
        title={`Ordenar por ${label}`}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
        )}
      </button>
    </th>
  );
};

const BookingsTab = () => {
  const [data, setData] = useState({ total: 0, bookings: [] });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedBookings = [...data.bookings].sort((a, b) => {
    const numeric = sortField === 'passengers' || sortField === 'total_price';
    let av = a[sortField];
    let bv = b[sortField];
    let cmp;
    if (numeric) {
      cmp = (Number(av) || 0) - (Number(bv) || 0);
    } else {
      cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'es', { numeric: true, sensitivity: 'base' });
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminListBookings({ status: status || undefined, search: search || undefined });
      setData(d);
    } catch (e) { toast.error('Error cargando reservas'); }
    finally { setLoading(false); }
  }, [status, search]);

  const sendSurvey = async (b) => {
    if (!b.customer_email) { toast.error('Esta reserva no tiene email'); return; }
    if (!window.confirm(`¿Enviar la encuesta de satisfacción a ${b.customer_name || b.customer_email}?`)) return;
    setSendingId(b.id);
    try {
      const res = await api.adminSendSurveyIndividual(b.id);
      toast.success(`Encuesta enviada a ${res.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar la encuesta');
    } finally {
      setSendingId(null);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Buscar por cliente, email o tour..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="booking-search"
            className="w-full h-11 pl-10 pr-3 border border-gray-200 rounded-full focus:border-[#1D4E41] outline-none"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 px-4 border border-gray-200 rounded-full focus:border-[#1D4E41] outline-none">
          <option value="">Todos los estados</option>
          <option value="paid">Pagadas</option>
          <option value="pending">Pendientes</option>
          <option value="failed">Fallidas</option>
        </select>
        <button onClick={fetchData} className="h-11 px-5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full font-medium flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4" /> Refrescar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>
      ) : data.bookings.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Sin reservas</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <SortableTh field="customer_name" label="Cliente" align="left" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh field="tour_title" label="Tour" align="left" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh field="date" label="Fecha" align="left" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh field="passengers" label="Pax" align="right" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh field="total_price" label="Total" align="right" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh field="payment_status" label="Estado" align="left" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedBookings.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1A1A1A]">{b.customer_name}</div>
                      <div className="text-xs text-gray-500">{b.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{b.tour_title}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(b.date)}</td>
                    <td className="px-4 py-3 text-right">{b.passengers}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#1D4E41]">{fmtMoney(b.total_price)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        b.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                        b.payment_status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{b.payment_status || 'pending'}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => sendSurvey(b)}
                        disabled={sendingId === b.id || !b.customer_email}
                        data-testid={`send-survey-booking-${b.id}`}
                        title="Enviar encuesta de satisfacción a este cliente"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#1D4E41] text-[#1D4E41] rounded-full hover:bg-[#E8F5E9] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {sendingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Encuesta
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-500">Total: {data.total} reservas</div>
        </div>
      )}
    </div>
  );
};

// ===================== SURVEYS TAB =====================
const ANSWER_LABELS = {
  excelente: 'Excelente', muy_buena: 'Muy buena', buena: 'Buena', regular: 'Regular', deficiente: 'Deficiente',
  si_completamente: 'Sí, completamente', si_mayoria: 'Sí, en su mayoría', parcialmente: 'Parcialmente', no: 'No',
  si_definitivamente: 'Sí, definitivamente', probablemente_si: 'Probablemente sí', no_seguro: 'No estoy seguro(a)', probablemente_no: 'Probablemente no',
};
const NEGATIVE_ANSWERS = new Set(['regular', 'deficiente', 'parcialmente', 'no', 'no_seguro', 'probablemente_no']);
const FOLLOWUP_OPTIONS = [
  { id: 'atencion', label: 'Pendiente' },
  { id: 'en_progreso', label: 'En progreso' },
  { id: 'resuelto', label: 'Resuelto' },
];

const AnswerPill = ({ value }) => {
  if (!value) return <span className="text-gray-300">—</span>;
  const neg = NEGATIVE_ANSWERS.has(value);
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${neg ? 'bg-red-100 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
      {ANSWER_LABELS[value] || value}
    </span>
  );
};

const SurveyCard = ({ s, onSaved }) => {
  const isLow = (s.overall_rating || 0) <= 3 || s.followup_status === 'atencion';
  const [status, setStatus] = useState(s.followup_status === 'ok' ? '' : (s.followup_status || ''));
  const [action, setAction] = useState(s.corrective_action || '');
  const [responsible, setResponsible] = useState(s.responsible || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    if (!window.confirm(`¿Eliminar permanentemente esta encuesta de ${s.customer_name || s.customer_email}? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await api.adminDeleteSurvey(s.id);
      toast.success('Encuesta eliminada');
      onSaved?.();
    } catch (e) {
      toast.error('Error al eliminar la encuesta');
      setDeleting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.adminUpdateSurveyFollowup(s.id, {
        status: status || 'atencion',
        corrective_action: action,
        responsible,
      });
      toast.success('Seguimiento guardado');
      onSaved?.();
    } catch (e) {
      toast.error('Error al guardar seguimiento');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = {
    atencion: { label: 'Pendiente', cls: 'bg-red-100 text-red-700' },
    en_progreso: { label: 'En progreso', cls: 'bg-amber-100 text-amber-700' },
    resuelto: { label: 'Resuelto', cls: 'bg-emerald-100 text-emerald-700' },
  }[s.followup_status];

  return (
    <div className={`bg-white rounded-2xl border p-5 ${isLow ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-100'}`} data-testid={`survey-card-${s.id}`}>
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-bold text-[#1A1A1A]">{s.overall_rating ?? '—'}/5</span>
            {isLow && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Atención</span>}
            {statusBadge && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.cls}`}>{statusBadge.label}</span>}
            {s.would_recommend && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Recomienda</span>}
          </div>
          <p className="font-semibold text-[#1A1A1A]">{s.customer_name}</p>
          <p className="text-sm text-gray-500">{s.customer_email} · {s.tour_title}</p>
        </div>
        <div className="text-right text-xs text-gray-400 flex flex-col items-end gap-2">
          <p>{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</p>
          <button
            onClick={remove}
            disabled={deleting}
            data-testid={`survey-delete-${s.id}`}
            title="Eliminar encuesta"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-red-200 text-red-600 rounded-full hover:bg-red-50 disabled:opacity-40"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Eliminar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-3 text-sm">
        <div className="flex items-center justify-between gap-2"><span className="text-gray-500">Puntualidad</span><AnswerPill value={s.q1_punctuality} /></div>
        <div className="flex items-center justify-between gap-2"><span className="text-gray-500">Conductor/guía</span><AnswerPill value={s.q2_driver} /></div>
        <div className="flex items-center justify-between gap-2"><span className="text-gray-500">Limpieza/comodidad</span><AnswerPill value={s.q3_cleanliness} /></div>
        <div className="flex items-center justify-between gap-2"><span className="text-gray-500">Seguridad</span><AnswerPill value={s.q4_safety} /></div>
        <div className="flex items-center justify-between gap-2"><span className="text-gray-500">Recomendaría</span><AnswerPill value={s.q5_recommend} /></div>
      </div>

      {s.comment && (
        <div className={`px-4 py-3 rounded-lg text-sm italic mb-3 ${isLow ? 'bg-red-50 text-red-800 border-l-4 border-red-400' : 'bg-gray-50 text-gray-700'}`}>
          &ldquo;{s.comment}&rdquo;
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        data-testid={`survey-followup-toggle-${s.id}`}
        className="text-sm font-medium text-[#1D4E41] flex items-center gap-1 hover:underline"
      >
        <ClipboardList className="w-4 h-4" /> Seguimiento / Acción correctiva {open ? '▲' : '▼'}
      </button>

      {open && (
        <div className="mt-3 p-4 rounded-xl bg-[#FAFAF8] border border-gray-100 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid={`survey-status-${s.id}`}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white outline-none focus:border-[#1D4E41]"
              >
                <option value="">— Seleccionar —</option>
                {FOLLOWUP_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Responsable</label>
              <input
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
                data-testid={`survey-responsible-${s.id}`}
                placeholder="Nombre del responsable"
                className="w-full h-10 px-3 border border-gray-200 rounded-lg outline-none focus:border-[#1D4E41]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Acción correctiva</label>
            <textarea
              value={action}
              onChange={(e) => setAction(e.target.value)}
              rows={3}
              data-testid={`survey-action-${s.id}`}
              placeholder="¿Qué se hizo o se hará para resolver/mejorar?"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-[#1D4E41] resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <a href={`mailto:${s.customer_email}?subject=Travel Guide Costa Rica`} className="h-10 px-4 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full text-sm font-medium flex items-center gap-1"><Mail className="w-4 h-4" /> Contactar</a>
            <button onClick={save} disabled={saving} data-testid={`survey-save-followup-${s.id}`} className="h-10 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SurveysTab = () => {
  const [data, setData] = useState({ total: 0, avg_overall: 0, nps_score: null, pending_followups: 0, surveys: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all, atencion, resuelto
  const [search, setSearch] = useState('');
  const [campaign, setCampaign] = useState({ count: 0 });
  const [sending, setSending] = useState('');
  const [exporting, setExporting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', email: '', tour_title: '' });
  const [manualSending, setManualSending] = useState(false);
  const [view, setView] = useState('responses'); // 'responses' | 'invites'
  const [invites, setInvites] = useState({ total: 0, responded_count: 0, pending_count: 0, invites: [] });
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [inviteFilter, setInviteFilter] = useState('all'); // all | pending | responded
  const COPY_EMAIL = 'erick2901@gmail.com';

  const loadInvites = useCallback(async () => {
    setInvitesLoading(true);
    try {
      const d = await api.adminListSurveyInvites();
      setInvites(d);
    } catch (e) { toast.error('Error cargando invitaciones'); }
    finally { setInvitesLoading(false); }
  }, []);

  const resendInvite = async (inv) => {
    setResendingId(inv.booking_id);
    try {
      await api.adminSendSurveyIndividual(inv.booking_id);
      toast.success(`Recordatorio reenviado a ${inv.customer_email}`);
      loadInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reenviar');
    } finally {
      setResendingId(null);
    }
  };

  const sendManual = async () => {
    if (!manualForm.name.trim() || !manualForm.email.trim()) {
      toast.error('Nombre y correo son obligatorios');
      return;
    }
    setManualSending(true);
    try {
      const res = await api.adminSendSurveyManual({
        name: manualForm.name.trim(),
        email: manualForm.email.trim(),
        tour_title: manualForm.tour_title.trim() || undefined,
      });
      toast.success(`Encuesta enviada a ${res.sent_to}`);
      setManualForm({ name: '', email: '', tour_title: '' });
      setManualOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar la encuesta');
    } finally {
      setManualSending(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminListSurveys();
      setData(d);
    } catch (e) { toast.error('Error cargando encuestas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (view === 'invites') loadInvites();
  }, [view, loadInvites]);
  useEffect(() => {
    (async () => {
      try { setCampaign(await api.adminGetPaidCustomersCount()); } catch (e) { /* ignore */ }
    })();
  }, []);

  const filtered = data.surveys.filter(s => {
    if (statusFilter === 'atencion' && s.followup_status !== 'atencion') return false;
    if (statusFilter === 'resuelto' && s.followup_status !== 'resuelto') return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = `${s.customer_name} ${s.customer_email} ${s.tour_title} ${s.comment || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const pendingCount = data.pending_followups ?? data.surveys.filter(s => s.followup_status === 'atencion').length;

  const exportPdf = async () => {
    setExporting(true);
    try {
      const blob = await api.adminExportSurveysPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `encuestas-travelguide-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error al generar PDF'); }
    finally { setExporting(false); }
  };

  const sendCampaign = async (mode) => {
    if (mode === 'all') {
      const ok = window.confirm(`¿Enviar la encuesta a ${campaign.count} cliente(s) pagado(s)? Esta acción enviará correos reales y no se puede deshacer.`);
      if (!ok) return;
    }
    setSending(mode);
    try {
      const res = await api.adminSendSurveyCampaign({ mode, test_email: COPY_EMAIL, confirm: mode === 'all' });
      if (mode === 'test') {
        toast.success(`Correo de prueba enviado a ${res.sent_to}`);
      } else {
        toast.success(`Encuesta en envío a ${res.queued} cliente(s). Copia a ${res.copy_to}.`);
      }
    } catch (e) { toast.error('Error al enviar la campaña'); }
    finally { setSending(''); }
  };

  return (
    <div>
      {/* Action bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6 bg-white rounded-2xl border border-gray-100 p-4">
        <div>
          <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2"><Send className="w-4 h-4 text-[#1D4E41]" /> Campaña de encuesta</h3>
          <p className="text-xs text-gray-500 mt-1">{campaign.count} cliente(s) pagado(s) elegibles · Copia a {COPY_EMAIL}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setManualOpen(true)} data-testid="open-manual-survey-btn" className="h-11 px-4 border border-[#1D4E41] text-[#1D4E41] hover:bg-[#E8F5E9] rounded-full font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Cliente manual
          </button>
          <button onClick={exportPdf} disabled={exporting} data-testid="export-surveys-pdf-btn" className="h-11 px-4 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full font-medium flex items-center gap-2 disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Exportar PDF
          </button>
          <button onClick={() => sendCampaign('test')} disabled={sending} data-testid="send-survey-test-btn" className="h-11 px-4 border border-[#1D4E41] text-[#1D4E41] hover:bg-[#E8F5E9] rounded-full font-medium flex items-center gap-2 disabled:opacity-50">
            {sending === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Enviar prueba
          </button>
          <button onClick={() => sendCampaign('all')} disabled={sending || campaign.count === 0} data-testid="send-survey-all-btn" className="h-11 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-semibold flex items-center gap-2 disabled:opacity-50">
            {sending === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar a todos ({campaign.count})
          </button>
        </div>
      </div>

      {/* Manual customer survey modal */}
      {manualOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !manualSending && setManualOpen(false)} data-testid="manual-survey-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Enviar encuesta a cliente</h3>
              <button onClick={() => setManualOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-5">Para clientes pasados que no están en la plataforma. Solo necesitas nombre y correo.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre <span className="text-red-500">*</span></label>
                <input
                  value={manualForm.name}
                  onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                  data-testid="manual-survey-name"
                  placeholder="Nombre del cliente"
                  className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={manualForm.email}
                  onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                  data-testid="manual-survey-email"
                  placeholder="cliente@correo.com"
                  className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Servicio / Tour <span className="text-gray-400">(opcional)</span></label>
                <input
                  value={manualForm.tour_title}
                  onChange={(e) => setManualForm({ ...manualForm, tour_title: e.target.value })}
                  data-testid="manual-survey-tour"
                  placeholder="Ej. Airport Transfer"
                  className="w-full h-11 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setManualOpen(false)} disabled={manualSending} className="h-11 px-5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full font-medium">Cancelar</button>
              <button onClick={sendManual} disabled={manualSending} data-testid="manual-survey-send" className="h-11 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-semibold flex items-center gap-2 disabled:opacity-50">
                {manualSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar encuesta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View toggle: Responses vs Invited */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-full w-fit mb-6">
        <button onClick={() => setView('responses')} data-testid="view-responses-btn" className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${view === 'responses' ? 'bg-white text-[#1D4E41] shadow-sm' : 'text-gray-600'}`}>Respuestas ({data.total})</button>
        <button onClick={() => setView('invites')} data-testid="view-invites-btn" className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${view === 'invites' ? 'bg-white text-[#1D4E41] shadow-sm' : 'text-gray-600'}`}>Invitados / Pendientes</button>
      </div>

      {view === 'responses' && (
      <>
      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total respuestas</p>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data.total}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Promedio (1-5)</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-[#1A1A1A]">{data.avg_overall || 0}</p>
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Recomiendan</p>
          <p className="text-2xl font-bold text-emerald-600">{data.surveys.filter(s => s.would_recommend).length}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Pendientes de acción</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-red-600">{pendingCount}</p>
            {pendingCount > 0 && <AlertTriangle className="w-5 h-5 text-red-600" />}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Buscar por cliente, tour o comentario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="survey-search"
            className="w-full h-11 pl-10 pr-3 border border-gray-200 rounded-full focus:border-[#1D4E41] outline-none"
          />
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-full">
          {[
            { id: 'all', label: 'Todas' },
            { id: 'atencion', label: 'Pendientes' },
            { id: 'resuelto', label: 'Resueltas' }
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setStatusFilter(opt.id)}
              data-testid={`survey-filter-${opt.id}`}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${statusFilter === opt.id ? 'bg-white text-[#1D4E41] shadow-sm' : 'text-gray-600'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Sin encuestas todavía</p>
          <p className="text-xs text-gray-400 mt-2">Las respuestas aparecerán aquí cuando los clientes completen la encuesta</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <SurveyCard key={s.id} s={s} onSaved={load} />
          ))}
        </div>
      )}
      </>
      )}

      {view === 'invites' && (
      <>
        {/* Invited summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total clientes</p>
            <p className="text-2xl font-bold text-[#1A1A1A]">{invites.total}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Respondieron</p>
            <p className="text-2xl font-bold text-emerald-600">{invites.responded_count}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Pendientes</p>
            <p className="text-2xl font-bold text-amber-600">{invites.pending_count}</p>
          </div>
        </div>

        {/* Invite filter */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-full w-fit mb-6">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'pending', label: 'Pendientes' },
            { id: 'responded', label: 'Respondieron' }
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setInviteFilter(opt.id)}
              data-testid={`invite-filter-${opt.id}`}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${inviteFilter === opt.id ? 'bg-white text-[#1D4E41] shadow-sm' : 'text-gray-600'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {invitesLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>
        ) : invites.invites.filter(i => inviteFilter === 'all' || i.status === inviteFilter).length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay clientes para mostrar</p>
            <p className="text-xs text-gray-400 mt-2">Aquí aparecen todos los clientes con reservas pagadas y su estado (Respondió / Pendiente). Usa &quot;Reenviar&quot; para enviarles la encuesta.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Servicio</th>
                    <th className="text-left px-4 py-3">Enviada</th>
                    <th className="text-left px-4 py-3">Estado</th>
                    <th className="text-right px-4 py-3">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invites.invites.filter(i => inviteFilter === 'all' || i.status === inviteFilter).map((inv) => (
                    <tr key={inv.booking_id} className="hover:bg-gray-50" data-testid={`invite-row-${inv.booking_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#1A1A1A]">{inv.customer_name || '—'}</div>
                        <div className="text-xs text-gray-500">{inv.customer_email}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {inv.tour_title || '—'}
                        {inv.bookings_count > 1 && <span className="ml-1 text-xs text-gray-400">· {inv.bookings_count} reservas</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {inv.last_sent_at ? new Date(inv.last_sent_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                        {inv.sent_count > 1 && <span className="ml-1 text-gray-400">({inv.sent_count}x)</span>}
                      </td>
                      <td className="px-4 py-3">
                        {inv.status === 'responded' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <Star className="w-3 h-3 fill-emerald-600 text-emerald-600" /> Respondió{inv.overall_rating ? ` (${inv.overall_rating}/5)` : ''}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Loader2 className="w-3 h-3" /> Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {inv.status === 'pending' && (
                          <button
                            onClick={() => resendInvite(inv)}
                            disabled={resendingId === inv.booking_id}
                            data-testid={`resend-invite-${inv.booking_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#1D4E41] text-[#1D4E41] rounded-full hover:bg-[#E8F5E9] disabled:opacity-40 whitespace-nowrap"
                          >
                            {resendingId === inv.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Reenviar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
      )}
    </div>
  );
};

// ===================== TOURS TAB =====================
const ToursTab = () => {
  const [data, setData] = useState({ tours: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // tourId -> {price, description}
  const [saving, setSaving] = useState({});
  const [refreshingAI, setRefreshingAI] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminListTours();
      setData(d);
    } catch (e) { toast.error('Error cargando tours'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshVoiceAgent = async () => {
    setRefreshingAI(true);
    try {
      const res = await api.adminRefreshVoiceAgent();
      if (res?.success) toast.success(`Asistente IA actualizado (${res.tours_synced} tours sincronizados)`);
      else toast.error('No se pudo actualizar el Asistente IA');
    } catch (e) {
      toast.error('Error al actualizar el Asistente IA');
    } finally {
      setRefreshingAI(false);
    }
  };

  const startEdit = (tour) => {
    setEditing({ ...editing, [tour.id]: { price: tour.price, description: tour.description } });
  };
  const cancelEdit = (tourId) => {
    const next = { ...editing };
    delete next[tourId];
    setEditing(next);
  };
  const saveTour = async (tourId) => {
    const e = editing[tourId];
    if (!e) return;
    setSaving({ ...saving, [tourId]: true });
    try {
      await api.adminPatchTour(tourId, { price: parseFloat(e.price), description: e.description });
      toast.success('Tour actualizado');
      cancelEdit(tourId);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving({ ...saving, [tourId]: false });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex-1 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800">
          ℹ️ <strong>Editar precio y descripción</strong>: los cambios se reflejan en el sitio público de inmediato. Los precios son NETOS (el IVA 13% se agrega automáticamente al checkout).
        </div>
        <button
          onClick={refreshVoiceAgent}
          disabled={refreshingAI}
          data-testid="refresh-voice-agent-btn"
          className="h-11 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full text-sm font-semibold flex items-center justify-center gap-2 flex-shrink-0 disabled:opacity-50"
          title="Sincroniza los tours actuales con el Asistente de Voz IA"
        >
          {refreshingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Actualizar Asistente IA
        </button>
      </div>

      <div className="space-y-4">
        {data.tours.map((tour) => {
          const isEditing = editing[tour.id];
          const isSaving = saving[tour.id];
          return (
            <div key={tour.id} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex flex-col md:flex-row gap-4">
                {tour.image_url && (
                  <img src={tour.image_url} alt={tour.title} className="w-full md:w-32 h-24 object-cover rounded-lg flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1A1A1A] truncate">{tour.title}</h3>
                      <p className="text-xs text-gray-500">{tour.category} · {tour.duration} · max {tour.max_passengers} pax</p>
                    </div>
                    {!isEditing && (
                      <button onClick={() => startEdit(tour)} data-testid={`edit-tour-${tour.id}`} className="px-3 py-1.5 text-sm border border-gray-200 hover:bg-gray-50 rounded-full flex items-center gap-1 flex-shrink-0">
                        <Edit3 className="w-3.5 h-3.5" /> Editar
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Precio NETO (USD) — IVA 13% se agrega al checkout</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={isEditing.price}
                          onChange={(e) => setEditing({ ...editing, [tour.id]: { ...isEditing, price: e.target.value } })}
                          data-testid={`price-input-${tour.id}`}
                          className="w-full max-w-xs h-10 px-3 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
                        <textarea
                          rows={4}
                          value={isEditing.description}
                          onChange={(e) => setEditing({ ...editing, [tour.id]: { ...isEditing, description: e.target.value } })}
                          data-testid={`desc-input-${tour.id}`}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#1D4E41] outline-none resize-none text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveTour(tour.id)} disabled={isSaving} data-testid={`save-tour-${tour.id}`} className="h-10 px-4 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-medium flex items-center gap-2 disabled:opacity-50">
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                        </button>
                        <button onClick={() => cancelEdit(tour.id)} className="h-10 px-4 border border-gray-200 hover:bg-gray-50 rounded-full font-medium">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-[#1D4E41] mt-1">${tour.price}</p>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{tour.description}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const emptyReview = { author: '', source: 'google', rating: 5, title: '', content: '', date: '', photos: [] };

const ReviewFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial ? { ...emptyReview, ...initial, photos: initial.photos || [] } : emptyReview);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { data_url } = await api.adminUploadReviewPhoto(file);
        setForm(f => ({ ...f, photos: [...(f.photos || []), data_url] }));
      }
      toast.success('Foto(s) agregada(s)');
    } catch {
      toast.error('Error al subir la foto');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removePhoto = (idx) => setForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.author.trim() || !form.title.trim() || !form.content.trim() || !form.date.trim()) {
      toast.error('Completa autor, título, reseña y fecha');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        author: form.author.trim(),
        source: form.source,
        rating: Number(form.rating) || 5,
        title: form.title.trim(),
        content: form.content.trim(),
        date: form.date.trim(),
        photos: form.photos || [],
      };
      if (isEdit) await api.adminUpdateReview(initial.id, payload);
      else await api.adminCreateReview(payload);
      toast.success(isEdit ? 'Reseña actualizada' : 'Reseña agregada');
      onSaved();
    } catch (e) {
      toast.error('Error al guardar la reseña');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="review-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{isEdit ? 'Editar reseña' : 'Agregar reseña'}</h3>
          <button onClick={onClose} data-testid="review-modal-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Plataforma</label>
              <select value={form.source} onChange={e => set('source', e.target.value)} data-testid="review-source" className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white outline-none focus:border-[#1D4E41]">
                <option value="google">Google</option>
                <option value="tripadvisor">Tripadvisor</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Estrellas</label>
              <select value={form.rating} onChange={e => set('rating', e.target.value)} data-testid="review-rating" className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white outline-none focus:border-[#1D4E41]">
                {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} ★</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Autor</label>
            <input value={form.author} onChange={e => set('author', e.target.value)} data-testid="review-author" placeholder="Nombre del cliente" className="w-full h-10 px-3 border border-gray-200 rounded-lg outline-none focus:border-[#1D4E41]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha</label>
            <input value={form.date} onChange={e => set('date', e.target.value)} data-testid="review-date" placeholder="Ej: June 2026" className="w-full h-10 px-3 border border-gray-200 rounded-lg outline-none focus:border-[#1D4E41]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Título</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} data-testid="review-title" placeholder="Título corto de la reseña" className="w-full h-10 px-3 border border-gray-200 rounded-lg outline-none focus:border-[#1D4E41]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reseña</label>
            <textarea value={form.content} onChange={e => set('content', e.target.value)} rows={4} data-testid="review-content" placeholder="Texto de la reseña" className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-[#1D4E41] resize-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fotos (opcional)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(form.photos || []).map((src, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200" data-testid={`review-photo-thumb-${i}`}>
                  <img src={src} alt={`foto-${i}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`review-photo-remove-${i}`} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 hover:border-[#1D4E41] flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:text-[#1D4E41] transition-colors" data-testid="review-photo-upload-label">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                <span className="text-[10px] mt-1">Subir</span>
                <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} data-testid="review-photo-input" className="hidden" disabled={uploading} />
              </label>
            </div>
            <p className="text-[11px] text-gray-400">Las imágenes se optimizan automáticamente. Recomendado: fotos reales del tour o del cliente.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="h-10 px-4 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full text-sm font-medium">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="review-save-btn" className="h-10 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setReviews(await api.adminListReviews()); }
    catch { toast.error('Error al cargar reseñas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la reseña de ${r.author}?`)) return;
    setDeletingId(r.id);
    try { await api.adminDeleteReview(r.id); toast.success('Reseña eliminada'); load(); }
    catch { toast.error('Error al eliminar'); setDeletingId(null); }
  };

  return (
    <div data-testid="reviews-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A]">Reseñas</h2>
          <p className="text-sm text-gray-500">Agrega o edita las reseñas que aparecen en la página de inicio.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="add-review-btn" className="h-10 px-5 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full text-sm font-semibold flex items-center gap-1">
          <Plus className="w-4 h-4" /> Agregar reseña
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1D4E41]" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aún no hay reseñas. Agrega la primera.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(r => (
            <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.source === 'google' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {r.source === 'google' ? 'Google' : 'Tripadvisor'}
                  </span>
                  <span className="inline-flex items-center text-[#FFB400] text-xs">{'★'.repeat(r.rating || 5)}</span>
                  <span className="text-xs text-gray-400">{r.date}</span>
                </div>
                <p className="font-semibold text-[#1A1A1A] truncate">{r.title}</p>
                <p className="text-sm text-gray-600 line-clamp-2">{r.content}</p>
                <p className="text-xs text-[#1D4E41] font-medium mt-1">— {r.author}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(r)} data-testid={`edit-review-${r.id}`} className="p-2 text-gray-500 hover:text-[#1D4E41] hover:bg-gray-50 rounded-lg" title="Editar"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => remove(r)} disabled={deletingId === r.id} data-testid={`delete-review-${r.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40" title="Eliminar">
                  {deletingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ReviewFormModal
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

// ===================== MAIN ADMIN PAGE =====================
const AdminPage = () => {
  const [authState, setAuthState] = useState({ loading: true, user: null });
  const [tab, setTab] = useState('customers');
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    // Skip /me check if we're in the middle of OAuth callback
    if (window.location.hash?.includes('session_id=')) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_error')) setAuthError(true);
    (async () => {
      try {
        const u = await api.authMe();
        setAuthState({ loading: false, user: u });
      } catch (e) {
        setAuthState({ loading: false, user: null });
      }
    })();
  }, []);

  const handleLogout = async () => {
    try { await api.authLogout(); } catch (e) { /* ignore */ }
    setAuthState({ loading: false, user: null });
    toast.success('Sesión cerrada');
  };

  if (authState.loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#1D4E41] animate-spin" />
      </div>
    );
  }

  if (!authState.user) {
    return <LoginView error={authError} />;
  }

  // Authorized but not admin (allowlist enforced)
  if (!authState.user.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
            <ShieldAlert className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-3">Acceso denegado</h1>
          <p className="text-gray-600 mb-6">Tu cuenta ({authState.user.email}) no está autorizada para acceder al panel.</p>
          <button onClick={handleLogout} className="h-11 px-6 bg-[#1D4E41] text-white rounded-full font-medium">Cerrar sesión</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1D4E41] rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1A1A1A] leading-tight">Panel Administrativo</h1>
              <p className="text-xs text-gray-500">Travel Guide Costa Rica</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm">
              {authState.user.picture && <img src={authState.user.picture} alt="" className="w-8 h-8 rounded-full" />}
              <span className="text-gray-600">{authState.user.name || authState.user.email}</span>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" className="h-9 px-4 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-full text-sm font-medium flex items-center gap-1">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
        <nav className="container mx-auto px-4 md:px-6 max-w-7xl flex gap-1 border-t border-gray-100 overflow-x-auto">
          {[
            { id: 'customers', label: 'Clientes', icon: Users },
            { id: 'bookings', label: 'Reservas', icon: Calendar },
            { id: 'surveys', label: 'Encuestas', icon: MessageSquare },
            { id: 'reviews', label: 'Reseñas', icon: Star },
            { id: 'tours', label: 'Tours', icon: Package }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${tab === t.id ? 'border-[#1D4E41] text-[#1D4E41]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        {tab === 'customers' && <CustomersTab />}
        {tab === 'bookings' && <BookingsTab />}
        {tab === 'surveys' && <SurveysTab />}
        {tab === 'reviews' && <ReviewsTab />}
        {tab === 'tours' && <ToursTab />}
      </main>
    </div>
  );
};

export default AdminPage;
